import { ApiError, apiDownloadResponse, apiRequest } from "../api/client";
import type {
  SimpleBillingDisplayError,
  SimpleBillingCancellationInput,
  SimpleBillingCollectionsSummaryResponse,
  SimpleBillingBusinessSummaryResponse,
  SimpleBillingInvoiceDetailResponse,
  SimpleBillingInvoiceFilters,
  SimpleBillingFinalizationInput,
  SimpleBillingInvoiceInput,
  SimpleBillingInvoiceListResponse,
  SimpleBillingPaymentHistoryResponse,
  SimpleBillingPaymentCorrectionInput,
  SimpleBillingPaymentCorrectionOperationResponse,
  SimpleBillingExpenseFilters,
  SimpleBillingExpenseCorrectionInput,
  SimpleBillingExpenseInput,
  SimpleBillingExpenseListResponse,
  SimpleBillingExpenseOperationResponse,
  SimpleBillingExpenseVoidInput,
  SimpleBillingPaymentInput,
  SimpleBillingPaymentOperationResponse,
  SimpleBillingPaymentReversalInput,
  SimpleBillingPendingPaymentFilters,
  SimpleBillingPendingPaymentListResponse,
  SimpleBillingProductInput,
  SimpleBillingProductResponse,
  SimpleBillingProductsResponse,
  SimpleBillingRateInput,
  SimpleBillingRateResponse,
  SimpleBillingRatesResponse,
  SimpleBillingSettingsInput,
  SimpleBillingReceivablesSettingsInput,
  SimpleBillingReceivablesSettingsResponse,
  SimpleBillingSettingsResponse,
  SimpleBillingSalesSummaryResponse,
  SimpleBillingInventoryStatus,
  SimpleBillingInventoryProductPolicy,
  SimpleBillingInventoryBalancesResponse,
  SimpleBillingInventoryMovementsResponse,
  SimpleBillingInventoryMovement,
  SimpleBillingInventoryTrackingMode,
  SimpleBillingMeasurementStatus,
  SimpleBillingProductMeasurementInput,
  SimpleBillingProductMeasurementResponse,
  SimpleBillingMeasurementInput,
  SimpleBillingMeasurementSnapshot,
  SimpleBillingTileMeasurementInput,
  SimpleBillingTileMeasurementSnapshot,
  SimpleBillingComparativeEstimate,
  SimpleBillingComparativeEstimateInput,
  SimpleBillingComparativeEstimateListResponse,
  SimpleBillingComparativeEstimateConvertInput,
  SimpleBillingComparativeEstimateConvertResponse,
  SimpleBillingComparativeEstimateExpireInput,
  SimpleBillingComparativeEstimateIssueInput,
  SimpleBillingComparativeEstimateResponseInput,
} from "./types";

export const SIMPLE_BILLING_API_BASE = "/api/simple-billing";

export function simpleBillingRequest<T>(path = "", init: RequestInit = {}): Promise<T> {
  const suffix = path.trim();
  const normalized = suffix && !suffix.startsWith("/") ? `/${suffix}` : suffix;
  return apiRequest<T>(`${SIMPLE_BILLING_API_BASE}${normalized}`, init);
}

const DISPLAYABLE_ERROR_STATUSES = new Set([401, 402, 403, 404, 409, 422]);

export function simpleBillingDisplayError(error: unknown): SimpleBillingDisplayError {
  if (error instanceof ApiError) {
    const message = DISPLAYABLE_ERROR_STATUSES.has(error.status)
      ? String(error.payload.error || error.message)
      : "Simple Billing is temporarily unavailable. Try again.";
    return {
      status: error.status,
      message,
      requestId: typeof error.payload.request_id === "string" ? error.payload.request_id : undefined,
    };
  }
  return {
    status: null,
    message: error instanceof Error ? error.message : "Simple Billing is temporarily unavailable. Try again.",
  };
}

function jsonRequest(method: "POST" | "PATCH" | "PUT", body: object): RequestInit {
  return { method, body: JSON.stringify(body) };
}

function inventoryTenantQuery(tenantId?: string | null): string {
  const normalized = tenantId?.trim();
  return normalized ? `?tenant_id=${encodeURIComponent(normalized)}` : "";
}

export function getSimpleBillingInventoryStatus(tenantId?: string | null): Promise<SimpleBillingInventoryStatus> {
  return simpleBillingRequest<SimpleBillingInventoryStatus>(`/inventory/status${inventoryTenantQuery(tenantId)}`);
}

export function setSimpleBillingInventoryAccess(input: {
  tenant_id: string;
  enabled: boolean;
  reason: string;
}): Promise<SimpleBillingInventoryStatus> {
  return simpleBillingRequest<SimpleBillingInventoryStatus>("/inventory/access", jsonRequest("PATCH", input));
}

export function listSimpleBillingInventoryPolicies(tenantId?: string | null): Promise<{ policies: SimpleBillingInventoryProductPolicy[] }> {
  const query = new URLSearchParams({ page: "1", page_size: "100" });
  if (tenantId?.trim()) query.set("tenant_id", tenantId.trim());
  return simpleBillingRequest<{ policies: SimpleBillingInventoryProductPolicy[] }>(`/inventory/products?${query.toString()}`);
}

export function configureSimpleBillingInventoryProduct(
  productId: string,
  input: { tracking_mode: SimpleBillingInventoryTrackingMode; stock_unit_name: string | null },
): Promise<SimpleBillingInventoryProductPolicy> {
  return simpleBillingRequest<SimpleBillingInventoryProductPolicy>(
    `/inventory/products/${encodeURIComponent(productId)}`,
    jsonRequest("PUT", input),
  );
}

function inventoryIdempotencyHeaders(): HeadersInit {
  const value = typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return { "Idempotency-Key": value };
}

export function receiveSimpleBillingStock(input: {
  product_id: string;
  quantity: string;
  received_on: string;
  source_reference: string | null;
  notes: string | null;
}): Promise<SimpleBillingInventoryMovement> {
  return simpleBillingRequest<SimpleBillingInventoryMovement>("/inventory/receipts", {
    ...jsonRequest("POST", input),
    headers: inventoryIdempotencyHeaders(),
  });
}

export function adjustSimpleBillingStock(input: {
  product_id: string;
  direction: "IN" | "OUT";
  quantity: string;
  occurred_on: string;
  reason: string;
}): Promise<SimpleBillingInventoryMovement> {
  return simpleBillingRequest<SimpleBillingInventoryMovement>("/inventory/adjustments", {
    ...jsonRequest("POST", input),
    headers: inventoryIdempotencyHeaders(),
  });
}

export function listSimpleBillingInventoryBalances(tenantId?: string | null): Promise<SimpleBillingInventoryBalancesResponse> {
  const query = new URLSearchParams({ page: "1", page_size: "100" });
  if (tenantId?.trim()) query.set("tenant_id", tenantId.trim());
  return simpleBillingRequest<SimpleBillingInventoryBalancesResponse>(`/inventory/balances?${query.toString()}`);
}

export function listSimpleBillingInventoryMovements(tenantId?: string | null): Promise<SimpleBillingInventoryMovementsResponse> {
  const query = new URLSearchParams({ page: "1", page_size: "50" });
  if (tenantId?.trim()) query.set("tenant_id", tenantId.trim());
  return simpleBillingRequest<SimpleBillingInventoryMovementsResponse>(`/inventory/movements?${query.toString()}`);
}

export function getSimpleBillingSettings(): Promise<SimpleBillingSettingsResponse> {
  return simpleBillingRequest<SimpleBillingSettingsResponse>("/settings");
}

export function createSimpleBillingSettings(input: SimpleBillingSettingsInput): Promise<SimpleBillingSettingsResponse> {
  return simpleBillingRequest<SimpleBillingSettingsResponse>("/settings", jsonRequest("POST", input));
}

export function updateSimpleBillingSettings(input: SimpleBillingSettingsInput): Promise<SimpleBillingSettingsResponse> {
  return simpleBillingRequest<SimpleBillingSettingsResponse>("/settings", jsonRequest("PATCH", input));
}

export function getSimpleBillingReceivablesSettings(): Promise<SimpleBillingReceivablesSettingsResponse> {
  return simpleBillingRequest<SimpleBillingReceivablesSettingsResponse>("/receivables-settings");
}

export function activateSimpleBillingReceivables(
  input: SimpleBillingReceivablesSettingsInput,
): Promise<SimpleBillingReceivablesSettingsResponse> {
  return simpleBillingRequest<SimpleBillingReceivablesSettingsResponse>(
    "/receivables-settings/activate",
    jsonRequest("POST", input),
  );
}

export function updateSimpleBillingReceivablesSettings(
  input: SimpleBillingReceivablesSettingsInput,
): Promise<SimpleBillingReceivablesSettingsResponse> {
  return simpleBillingRequest<SimpleBillingReceivablesSettingsResponse>(
    "/receivables-settings",
    jsonRequest("PATCH", input),
  );
}

export function listSimpleBillingProducts(includeInactive = false): Promise<SimpleBillingProductsResponse> {
  return simpleBillingRequest<SimpleBillingProductsResponse>(`/products?include_inactive=${includeInactive ? "true" : "false"}`);
}

export function getSimpleBillingProduct(productId: string): Promise<SimpleBillingProductResponse> {
  return simpleBillingRequest<SimpleBillingProductResponse>(`/products/${encodeURIComponent(productId)}`);
}

export function createSimpleBillingProduct(input: SimpleBillingProductInput): Promise<SimpleBillingProductResponse> {
  return simpleBillingRequest<SimpleBillingProductResponse>("/products", jsonRequest("POST", input));
}

export function updateSimpleBillingProduct(productId: string, input: SimpleBillingProductInput): Promise<SimpleBillingProductResponse> {
  return simpleBillingRequest<SimpleBillingProductResponse>(`/products/${encodeURIComponent(productId)}`, jsonRequest("PATCH", input));
}

export function disableSimpleBillingProduct(productId: string): Promise<SimpleBillingProductResponse> {
  return simpleBillingRequest<SimpleBillingProductResponse>(`/products/${encodeURIComponent(productId)}/disable`, jsonRequest("POST", {}));
}

export function getSimpleBillingMeasurementStatus(tenantId?: string | null): Promise<SimpleBillingMeasurementStatus> {
  return simpleBillingRequest<SimpleBillingMeasurementStatus>(`/measurements/status${inventoryTenantQuery(tenantId)}`);
}

export function configureSimpleBillingProductMeasurement(
  productId: string,
  input: SimpleBillingProductMeasurementInput,
): Promise<SimpleBillingProductMeasurementResponse> {
  return simpleBillingRequest<SimpleBillingProductMeasurementResponse>(
    `/measurements/products/${encodeURIComponent(productId)}`,
    jsonRequest("PUT", input),
  );
}

export function previewSimpleBillingTileMeasurement(
  productId: string,
  input: SimpleBillingTileMeasurementInput,
): Promise<Omit<SimpleBillingTileMeasurementSnapshot, "coverage_source" | "tile_profile">> {
  return simpleBillingRequest(
    `/measurements/products/${encodeURIComponent(productId)}/preview`,
    jsonRequest("POST", input),
  );
}

export function previewSimpleBillingMeasurement(
  productId: string,
  input: SimpleBillingMeasurementInput,
): Promise<Omit<SimpleBillingMeasurementSnapshot, "tile_profile" | "timber_profile" | "coverage_source">> {
  return simpleBillingRequest(
    `/measurements/products/${encodeURIComponent(productId)}/preview`,
    jsonRequest("POST", input),
  );
}

export function createSimpleBillingComparativeEstimate(
  input: SimpleBillingComparativeEstimateInput,
): Promise<SimpleBillingComparativeEstimate> {
  return simpleBillingRequest<SimpleBillingComparativeEstimate>(
    "/comparative-estimates",
    jsonRequest("POST", input),
  );
}

export function listSimpleBillingComparativeEstimates(
  tenantId?: string | null,
  page = 1,
  pageSize = 20,
): Promise<SimpleBillingComparativeEstimateListResponse> {
  const query = new URLSearchParams({
    page: String(Math.max(1, Math.trunc(page))),
    page_size: String(Math.min(100, Math.max(1, Math.trunc(pageSize)))),
  });
  if (tenantId?.trim()) query.set("tenant_id", tenantId.trim());
  return simpleBillingRequest<SimpleBillingComparativeEstimateListResponse>(
    `/comparative-estimates?${query.toString()}`,
  );
}

export function getSimpleBillingComparativeEstimate(
  estimateId: string,
  tenantId?: string | null,
): Promise<SimpleBillingComparativeEstimate> {
  return simpleBillingRequest<SimpleBillingComparativeEstimate>(
    `/comparative-estimates/${encodeURIComponent(estimateId)}${inventoryTenantQuery(tenantId)}`,
  );
}

export function convertSimpleBillingComparativeEstimate(
  estimateId: string,
  input: SimpleBillingComparativeEstimateConvertInput,
  idempotencyKey: string,
): Promise<SimpleBillingComparativeEstimateConvertResponse> {
  return simpleBillingRequest<SimpleBillingComparativeEstimateConvertResponse>(
    `/comparative-estimates/${encodeURIComponent(estimateId)}/convert`,
    {
      ...jsonRequest("POST", input),
      headers: { "Idempotency-Key": idempotencyKey },
    },
  );
}

export function issueSimpleBillingComparativeEstimate(
  estimateId: string,
  input: SimpleBillingComparativeEstimateIssueInput,
): Promise<SimpleBillingComparativeEstimate> {
  return simpleBillingRequest<SimpleBillingComparativeEstimate>(
    `/comparative-estimates/${encodeURIComponent(estimateId)}/issue`,
    jsonRequest("POST", input),
  );
}

export function respondToSimpleBillingComparativeEstimate(
  estimateId: string,
  input: SimpleBillingComparativeEstimateResponseInput,
): Promise<SimpleBillingComparativeEstimate> {
  return simpleBillingRequest<SimpleBillingComparativeEstimate>(
    `/comparative-estimates/${encodeURIComponent(estimateId)}/respond`,
    jsonRequest("POST", input),
  );
}

export function expireSimpleBillingComparativeEstimate(
  estimateId: string,
  input: SimpleBillingComparativeEstimateExpireInput,
): Promise<SimpleBillingComparativeEstimate> {
  return simpleBillingRequest<SimpleBillingComparativeEstimate>(
    `/comparative-estimates/${encodeURIComponent(estimateId)}/expire`,
    jsonRequest("POST", input),
  );
}

export async function getSimpleBillingComparativeEstimatePdf(
  estimateId: string,
  tenantId?: string | null,
): Promise<SimpleBillingPdfResponse> {
  const response = await apiDownloadResponse(
    `${SIMPLE_BILLING_API_BASE}/comparative-estimates/${encodeURIComponent(estimateId)}/pdf${inventoryTenantQuery(tenantId)}`,
  );
  return {
    blob: response.blob,
    filename: filenameFromDisposition(response.headers.get("Content-Disposition")),
  };
}

export function listSimpleBillingRates(productId: string): Promise<SimpleBillingRatesResponse> {
  return simpleBillingRequest<SimpleBillingRatesResponse>(`/products/${encodeURIComponent(productId)}/rates`);
}

export function createSimpleBillingRate(productId: string, input: SimpleBillingRateInput): Promise<SimpleBillingRateResponse> {
  return simpleBillingRequest<SimpleBillingRateResponse>(`/products/${encodeURIComponent(productId)}/rates`, jsonRequest("POST", input));
}

export function updateSimpleBillingRate(productId: string, rateId: string, input: SimpleBillingRateInput): Promise<SimpleBillingRateResponse> {
  return simpleBillingRequest<SimpleBillingRateResponse>(
    `/products/${encodeURIComponent(productId)}/rates/${encodeURIComponent(rateId)}`,
    jsonRequest("PATCH", input),
  );
}

export function disableSimpleBillingRate(productId: string, rateId: string): Promise<SimpleBillingRateResponse> {
  return simpleBillingRequest<SimpleBillingRateResponse>(
    `/products/${encodeURIComponent(productId)}/rates/${encodeURIComponent(rateId)}/disable`,
    jsonRequest("POST", {}),
  );
}

export function createSimpleBillingInvoice(input: SimpleBillingInvoiceInput): Promise<SimpleBillingInvoiceDetailResponse> {
  return simpleBillingRequest<SimpleBillingInvoiceDetailResponse>("/invoices", jsonRequest("POST", input));
}

function tenantQuery(tenantId?: string | null): string {
  const normalized = tenantId?.trim();
  return normalized ? `?tenant_id=${encodeURIComponent(normalized)}` : "";
}

const ALLOWED_INVOICE_PAGE_SIZES = new Set([10, 25, 50, 100]);

export function listSimpleBillingInvoices(
  filters: SimpleBillingInvoiceFilters,
  tenantId?: string | null,
): Promise<SimpleBillingInvoiceListResponse> {
  if (!ALLOWED_INVOICE_PAGE_SIZES.has(filters.page_size)) {
    throw new Error("Bill page size must be 10, 25, 50, or 100");
  }
  const query = new URLSearchParams({
    page: String(Math.max(1, Math.trunc(filters.page))),
    page_size: String(filters.page_size),
  });
  for (const key of ["date_from", "date_to", "status", "invoice_number", "customer_name"] as const) {
    const value = filters[key]?.trim();
    if (value) query.set(key, value);
  }
  const normalizedTenantId = tenantId?.trim();
  if (normalizedTenantId) query.set("tenant_id", normalizedTenantId);
  return simpleBillingRequest<SimpleBillingInvoiceListResponse>(`/invoices?${query.toString()}`);
}

export function listSimpleBillingPendingPayments(
  filters: SimpleBillingPendingPaymentFilters,
  tenantId?: string | null,
): Promise<SimpleBillingPendingPaymentListResponse> {
  if (!ALLOWED_INVOICE_PAGE_SIZES.has(filters.page_size)) {
    throw new Error("Pending payment page size must be 10, 25, 50, or 100");
  }
  const query = new URLSearchParams({
    page: String(Math.max(1, Math.trunc(filters.page))),
    page_size: String(filters.page_size),
  });
  for (const key of ["status", "invoice_number", "customer_name", "due_from", "due_to"] as const) {
    const value = filters[key]?.trim();
    if (value) query.set(key, value);
  }
  const normalizedTenantId = tenantId?.trim();
  if (normalizedTenantId) query.set("tenant_id", normalizedTenantId);
  return simpleBillingRequest<SimpleBillingPendingPaymentListResponse>(
    `/pending-payments?${query.toString()}`,
  );
}

export function getSimpleBillingSalesSummary(
  dateFrom: string,
  dateTo: string,
  tenantId?: string | null,
): Promise<SimpleBillingSalesSummaryResponse> {
  const query = new URLSearchParams({ date_from: dateFrom, date_to: dateTo });
  const normalizedTenantId = tenantId?.trim();
  if (normalizedTenantId) query.set("tenant_id", normalizedTenantId);
  return simpleBillingRequest<SimpleBillingSalesSummaryResponse>(
    `/reports/sales-summary?${query.toString()}`,
  );
}

export function getSimpleBillingCollectionsSummary(
  dateFrom: string,
  dateTo: string,
  tenantId?: string | null,
): Promise<SimpleBillingCollectionsSummaryResponse> {
  const query = new URLSearchParams({ date_from: dateFrom, date_to: dateTo });
  const normalizedTenantId = tenantId?.trim();
  if (normalizedTenantId) query.set("tenant_id", normalizedTenantId);
  return simpleBillingRequest<SimpleBillingCollectionsSummaryResponse>(
    `/reports/collections-summary?${query.toString()}`,
  );
}

export function getSimpleBillingBusinessSummary(
  dateFrom: string,
  dateTo: string,
  tenantId?: string | null,
): Promise<SimpleBillingBusinessSummaryResponse> {
  const query = new URLSearchParams({ date_from: dateFrom, date_to: dateTo });
  const normalizedTenantId = tenantId?.trim();
  if (normalizedTenantId) query.set("tenant_id", normalizedTenantId);
  return simpleBillingRequest<SimpleBillingBusinessSummaryResponse>(
    `/reports/business-summary?${query.toString()}`,
  );
}

export function getSimpleBillingInvoice(invoiceId: string, tenantId?: string | null): Promise<SimpleBillingInvoiceDetailResponse> {
  return simpleBillingRequest<SimpleBillingInvoiceDetailResponse>(
    `/invoices/${encodeURIComponent(invoiceId)}${tenantQuery(tenantId)}`,
  );
}

export function updateSimpleBillingInvoice(invoiceId: string, input: SimpleBillingInvoiceInput): Promise<SimpleBillingInvoiceDetailResponse> {
  return simpleBillingRequest<SimpleBillingInvoiceDetailResponse>(
    `/invoices/${encodeURIComponent(invoiceId)}`,
    jsonRequest("PATCH", input),
  );
}

export function finalizeSimpleBillingInvoice(
  invoiceId: string,
  input: SimpleBillingFinalizationInput = {},
): Promise<SimpleBillingInvoiceDetailResponse> {
  return simpleBillingRequest<SimpleBillingInvoiceDetailResponse>(
    `/invoices/${encodeURIComponent(invoiceId)}/finalize`,
    jsonRequest("POST", input),
  );
}

export function cancelSimpleBillingInvoice(
  invoiceId: string,
  input: SimpleBillingCancellationInput,
): Promise<SimpleBillingInvoiceDetailResponse> {
  return simpleBillingRequest<SimpleBillingInvoiceDetailResponse>(
    `/invoices/${encodeURIComponent(invoiceId)}/cancel`,
    jsonRequest("POST", input),
  );
}

export function getSimpleBillingPaymentHistory(
  invoiceId: string,
  tenantId?: string | null,
): Promise<SimpleBillingPaymentHistoryResponse> {
  return simpleBillingRequest<SimpleBillingPaymentHistoryResponse>(
    `/invoices/${encodeURIComponent(invoiceId)}/payments${tenantQuery(tenantId)}`,
  );
}

export function recordSimpleBillingPayment(
  invoiceId: string,
  input: SimpleBillingPaymentInput,
  idempotencyKey: string,
): Promise<SimpleBillingPaymentOperationResponse> {
  return simpleBillingRequest<SimpleBillingPaymentOperationResponse>(
    `/invoices/${encodeURIComponent(invoiceId)}/payments`,
    {
      ...jsonRequest("POST", input),
      headers: { "Idempotency-Key": idempotencyKey },
    },
  );
}

export function reverseSimpleBillingPayment(
  invoiceId: string,
  paymentId: string,
  input: SimpleBillingPaymentReversalInput,
  idempotencyKey: string,
): Promise<SimpleBillingPaymentOperationResponse> {
  return simpleBillingRequest<SimpleBillingPaymentOperationResponse>(
    `/invoices/${encodeURIComponent(invoiceId)}/payments/${encodeURIComponent(paymentId)}/reverse`,
    {
      ...jsonRequest("POST", input),
      headers: { "Idempotency-Key": idempotencyKey },
    },
  );
}

export function correctSimpleBillingPayment(
  invoiceId: string,
  paymentId: string,
  input: SimpleBillingPaymentCorrectionInput,
  idempotencyKey: string,
): Promise<SimpleBillingPaymentCorrectionOperationResponse> {
  return simpleBillingRequest<SimpleBillingPaymentCorrectionOperationResponse>(
    `/invoices/${encodeURIComponent(invoiceId)}/payments/${encodeURIComponent(paymentId)}/correct`,
    {
      ...jsonRequest("POST", input),
      headers: { "Idempotency-Key": idempotencyKey },
    },
  );
}

export function listSimpleBillingExpenses(
  filters: SimpleBillingExpenseFilters,
  tenantId?: string | null,
): Promise<SimpleBillingExpenseListResponse> {
  const query = new URLSearchParams({
    date_from: filters.date_from,
    date_to: filters.date_to,
    page: String(filters.page),
    page_size: String(filters.page_size),
  });
  if (filters.category) query.set("category", filters.category);
  if (filters.payment_mode) query.set("payment_mode", filters.payment_mode);
  if (filters.paid_by?.trim()) query.set("paid_by", filters.paid_by.trim());
  if (filters.funding_source) query.set("funding_source", filters.funding_source);
  if (tenantId?.trim()) query.set("tenant_id", tenantId.trim());
  return simpleBillingRequest<SimpleBillingExpenseListResponse>(
    `/expenses?${query.toString()}`,
  );
}

export function recordSimpleBillingExpense(
  input: SimpleBillingExpenseInput,
  idempotencyKey: string,
): Promise<SimpleBillingExpenseOperationResponse> {
  return simpleBillingRequest<SimpleBillingExpenseOperationResponse>(
    "/expenses",
    {
      ...jsonRequest("POST", input),
      headers: { "Idempotency-Key": idempotencyKey },
    },
  );
}

export function correctSimpleBillingExpense(
  expenseId: string,
  input: SimpleBillingExpenseCorrectionInput,
  idempotencyKey: string,
): Promise<SimpleBillingExpenseOperationResponse> {
  return simpleBillingRequest<SimpleBillingExpenseOperationResponse>(
    `/expenses/${encodeURIComponent(expenseId)}/correct`,
    {
      ...jsonRequest("POST", input),
      headers: { "Idempotency-Key": idempotencyKey },
    },
  );
}

export function voidSimpleBillingExpense(
  expenseId: string,
  input: SimpleBillingExpenseVoidInput,
  idempotencyKey: string,
): Promise<SimpleBillingExpenseOperationResponse> {
  return simpleBillingRequest<SimpleBillingExpenseOperationResponse>(
    `/expenses/${encodeURIComponent(expenseId)}/void`,
    {
      ...jsonRequest("POST", input),
      headers: { "Idempotency-Key": idempotencyKey },
    },
  );
}

export interface SimpleBillingPdfResponse {
  blob: Blob;
  filename: string | null;
}

function filenameFromDisposition(value: string | null): string | null {
  const matched = value?.match(/filename\s*=\s*(?:"([^"]+)"|([^;\s]+))/i);
  return matched?.[1] || matched?.[2] || null;
}

export async function getSimpleBillingInvoicePdf(
  invoiceId: string,
  disposition: "inline" | "attachment",
  tenantId?: string | null,
): Promise<SimpleBillingPdfResponse> {
  const query = new URLSearchParams({ disposition });
  const normalizedTenantId = tenantId?.trim();
  if (normalizedTenantId) query.set("tenant_id", normalizedTenantId);
  const response = await apiDownloadResponse(
    `${SIMPLE_BILLING_API_BASE}/invoices/${encodeURIComponent(invoiceId)}/pdf?${query.toString()}`,
  );
  return {
    blob: response.blob,
    filename: filenameFromDisposition(response.headers.get("Content-Disposition")),
  };
}

export async function getSimpleBillingPaymentReceiptPdf(
  invoiceId: string,
  paymentId: string,
  disposition: "inline" | "attachment",
  tenantId?: string | null,
): Promise<SimpleBillingPdfResponse> {
  const query = new URLSearchParams({ disposition });
  const normalizedTenantId = tenantId?.trim();
  if (normalizedTenantId) query.set("tenant_id", normalizedTenantId);
  const response = await apiDownloadResponse(
    `${SIMPLE_BILLING_API_BASE}/invoices/${encodeURIComponent(invoiceId)}`
      + `/payments/${encodeURIComponent(paymentId)}/receipt?${query.toString()}`,
  );
  return {
    blob: response.blob,
    filename: filenameFromDisposition(response.headers.get("Content-Disposition")),
  };
}

export async function getSimpleBillingBusinessSummaryPdf(
  dateFrom: string,
  dateTo: string,
  disposition: "inline" | "attachment",
  tenantId?: string | null,
): Promise<SimpleBillingPdfResponse> {
  const query = new URLSearchParams({
    date_from: dateFrom,
    date_to: dateTo,
    disposition,
  });
  const normalizedTenantId = tenantId?.trim();
  if (normalizedTenantId) query.set("tenant_id", normalizedTenantId);
  const response = await apiDownloadResponse(
    `${SIMPLE_BILLING_API_BASE}/reports/business-summary/pdf?${query.toString()}`,
  );
  return {
    blob: response.blob,
    filename: filenameFromDisposition(response.headers.get("Content-Disposition")),
  };
}
